import { useContext, useEffect, useLayoutEffect, useRef } from "react";
import { isThemeId, type ThemeId } from "./themeCatalog";
import { AppThemeContext, type AppThemeContextValue } from "./themeContext";

export function useAppTheme(): AppThemeContextValue {
  const context = useContext(AppThemeContext);
  if (!context) throw new Error("useAppTheme must be used within ThemeProvider.");
  return context;
}

/**
 * Lädt die Theme-Vorgaben der App über die Produkt-API, sobald sich `key` ändert (z. B. Mandant
 * oder Anmeldung). Der Lader muss auch ohne Anmeldung antworten (Anmeldeseiten).
 */
export function useThemeAvailabilitySync(load: () => Promise<unknown>, key: string) {
  const { registerAvailabilityLoader, reloadAvailability } = useAppTheme();
  const loadRef = useRef(load);
  useLayoutEffect(() => {
    loadRef.current = load;
  });
  useEffect(() => {
    const stable = () => loadRef.current();
    registerAvailabilityLoader(stable);
    void reloadAvailability();
    return () => registerAvailabilityLoader(null);
  }, [key, registerAvailabilityLoader, reloadAvailability]);
}

/** Für gemeinsame Bausteine, die auch ohne ThemeProvider dargestellt werden können. */
export function useOptionalAppTheme(): AppThemeContextValue | null {
  return useContext(AppThemeContext);
}

/**
 * Bindet eine serverseitig gespeicherte Benutzerwahl an: übernimmt den Serverwert, sobald er geladen
 * ist (`undefined` = noch nicht geladen), und speichert neue Wahlen über `persist`.
 */
export function useServerUserTheme(serverThemeId: string | null | undefined, persist: (id: ThemeId) => void) {
  const { adoptUserThemeId, registerUserThemePersistence } = useAppTheme();
  const persistRef = useRef(persist);
  useLayoutEffect(() => {
    persistRef.current = persist;
  });
  useEffect(() => {
    registerUserThemePersistence((id) => persistRef.current(id));
    return () => registerUserThemePersistence(null);
  }, [registerUserThemePersistence]);
  useEffect(() => {
    if (serverThemeId === undefined) return;
    adoptUserThemeId(isThemeId(serverThemeId) ? serverThemeId : null);
  }, [adoptUserThemeId, serverThemeId]);
}
